/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import Joi from 'joi';

export default function useHandleTemplateValidation() {
  function customTranslationSchema({
    message = 'validation.fieldRequired',
    key = 'en',
    ancestor = 3,
  }: {
    message?: string;
    key?: string;
    ancestor?: number;
  }) {
    return Joi.string()
      .allow(null, '')
      .messages({
        'string.empty': message,
        'string.required': message,
        'any.invalid': message,
      })
      .custom((value, helpers) => {
        const languagesFromState = helpers.state.ancestors[ancestor]?.languages as string[] | undefined;
        if (!languagesFromState) {
          return value;
        }
        /* eslint-disable-next-line */
        if (languagesFromState.includes(key) && (value === null || typeof value === 'undefined' || value.trim() === '')) {
          return helpers.error('any.invalid', { message });
        }
        return value;
      });
  }

  function translationFieldSchema({
    message = 'validation.fieldRequired',
    ancestor = 3,
  }: {
    message?: string;
    ancestor?: number;
  }) {
    return Joi.object({
      en: customTranslationSchema({ key: 'en', message, ancestor }),
      fr: customTranslationSchema({ key: 'fr', message, ancestor }),
      es: customTranslationSchema({ key: 'es', message, ancestor }),
    }).unknown(true);
  }

  // Joi schema for an OptionSchema item
  function optionSchema({ message = 'validation.fieldRequired', ancestor = 5 }: { message?: string; ancestor?: number }) {
    return Joi.object({
      id: Joi.string()
        .guid({ version: ['uuidv4'] })
        .required(),
      text: translationFieldSchema({ ancestor, message }),
      tag: Joi.string().allow(null, '').optional(),
    });
  }

  // Joi schema for DocumentReview part of a FieldSchema
  const DocumentReviewSchema = Joi.object({
    acknowledgeContent: translationFieldSchema({ ancestor: 4 }),
    description: translationFieldSchema({ ancestor: 4 }),
    confirmText: translationFieldSchema({ ancestor: 4 }),
    url: translationFieldSchema({ ancestor: 4, message: 'validation.urlRequired' }),
  }).allow(null);

  // Define FollowUpSchema using Joi.link() for older Joi versions
  const FollowUpSchema = Joi.object({
    id: Joi.string()
      .guid({ version: ['uuidv4'] })
      .required(),
    label: translationFieldSchema({ ancestor: 5 }),
    field: Joi.string().required(),
    inputType: Joi.string().required(),
    text: translationFieldSchema({ ancestor: 5 }),
    isInfo: Joi.boolean().optional(),
    order: Joi.number().integer().min(0).required(),
    optionList: Joi.array()
      .items(optionSchema({ ancestor: 7 }))
      .allow(null),
    isFollowUp: Joi.boolean().valid(true).required(),
    min: Joi.number().integer().min(0).allow(null),
    max: Joi.number()
      .integer()
      .min(Joi.ref('min', { adjust: (value: number) => value || 0 }))
      .allow(null),
    link: translationFieldSchema({ ancestor: 5 }).allow(null),
    parent: Joi.string()
      .guid({ version: ['uuidv4'] })
      .allow(null),
    optionId: Joi.string()
      .guid({ version: ['uuidv4'] })
      .allow(null)
      .required(),
    followUpQuestionList: Joi.array().items(Joi.link('#FollowUpSchema')).allow(null),

    section: Joi.string().optional().allow(null),
    sectionTitle: translationFieldSchema({ ancestor: 5 }),

    parentSection: Joi.string().allow(null),
    parentSectionTitle: translationFieldSchema({ ancestor: 5 }).allow(null),

    config: Joi.object().unknown(true).allow(null),
    disabled: Joi.boolean().optional(),
    disabledId: Joi.string().allow(null, '').optional(),
    isFrontEndOnly: Joi.boolean().optional(),
    hideFieldSelector: Joi.boolean().optional(),
    timestamp: Joi.number().optional(),
  }).id('FollowUpSchema');

  const QuestionSchema = Joi.object({
    id: Joi.string()
      .guid({ version: ['uuidv4'] })
      .required(),
    label: translationFieldSchema({ ancestor: 3 }),
    field: Joi.string().required(),
    inputType: Joi.string().required(),
    text: translationFieldSchema({ ancestor: 3 }),
    isInfo: Joi.boolean().optional(),
    order: Joi.number().integer().min(0).required(),
    optionList: Joi.array()
      .items(optionSchema({ ancestor: 5 }))
      .allow(null),
    isFollowUp: Joi.boolean().optional().default(false),
    followUpQuestionList: Joi.array().items(FollowUpSchema).allow(null),
    min: Joi.number().integer().min(0).allow(null),
    max: Joi.number()
      .integer()
      .min(Joi.ref('min', { adjust: (value: number) => value || 0 }))
      .allow(null),
    link: translationFieldSchema({ ancestor: 3 }).allow(null),
    parent: Joi.string()
      .guid({ version: ['uuidv4'] })
      .allow(null),
    optionId: Joi.string()
      .guid({ version: ['uuidv4'] })
      .allow(null),
    documentReview: DocumentReviewSchema,
    enableWorkPermit: Joi.boolean().optional(),

    section: Joi.string().optional().allow(null),
    sectionTitle: translationFieldSchema({ ancestor: 3 }),

    parentSection: Joi.string().allow(null),
    parentSectionTitle: translationFieldSchema({ ancestor: 3 }).allow(null),

    config: Joi.object().unknown(true).allow(null),
    disabled: Joi.boolean().optional(),
    disabledId: Joi.string().allow(null, '').optional(),
    isFrontEndOnly: Joi.boolean().optional(),
    hideFieldSelector: Joi.boolean().optional(),
    timestamp: Joi.number().optional(),
    followUpField: Joi.string().optional().allow(null),
  }).custom((value, helpers) => {
    if (value.followUpQuestionList && value.optionList) {
      const validOptionIds = value.optionList.map((option: { id: string }) => option.id);
      /* eslint-disable-next-line */
      for (const followUp of value.followUpQuestionList) {
        if (followUp.optionId && !validOptionIds.includes(followUp.optionId)) {
          return helpers.error('any.invalid', {
            message: 'validation.invalidFollowUpOptionId',
            path: [helpers.state.path, 'followUpQuestionList', value.followUpQuestionList.indexOf(followUp), 'optionId'],
          });
        }
      }
    }
    if (value.optionList) {
      const optionIds = value.optionList.map((option: { id: string }) => option.id);
      if (new Set(optionIds).size !== optionIds.length) {
        return helpers.error('any.invalid', {
          message: 'validation.duplicateOptionIds',
          path: [helpers.state.path, 'optionList'],
        });
      }
    }
    return value;
  });

  const QuestionsSchema = Joi.array()
    .items(QuestionSchema)
    .custom((values, helpers) => {
      const ids = values.map((item: { id: string }) => item.id);
      if (new Set(ids).size !== ids.length) {
        return helpers.error('array.unique', { message: 'validation.duplicateQuestionIdAcrossForm', path: [] });
      }
      return values;
    })
    .label('Form Questions');

  const TemplateSchema = Joi.object({
    name: Joi.string().max(50).required().messages({
      'any.required': 'validation.templateNameRequired',
      'string.empty': 'validation.templateNameRequired',
      'string.max': 'validation.templateNameMaxLength',
    }),
    questions: QuestionsSchema.required().label('Template Questions'),
    languages: Joi.array().items(Joi.string().valid('en', 'fr', 'es')).min(1).required().label('Active Languages'),
    defaultLanguage: Joi.string()
      .required()
      .custom((value, helpers) => {
        const { languages: activeLanguages } = helpers.state.ancestors[0];
        if (!activeLanguages || !activeLanguages.includes(value)) {
          return helpers.error('any.invalid', { message: 'validation.defaultLanguageNotInActive', path: ['defaultLanguage'] });
        }
        return value;
      })
      .label('Default Language'),
  });

  return { QuestionSchema, QuestionsSchema, TemplateSchema };
}
